import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HttpModule } from '@angular/http';
import { RouterModule } from '@angular/router';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';

import { ROUTES } from './app.routes';
import { AppComponent } from './app.component';
import { AuthGuard, GuestGuard, LoginGuard } from './services/authentication.service';
import { CanCreateGuard, NavGuard, NavGuardSettings } from './services/account-permissions.service';
import { MAT_DATE_LOCALE } from '@angular/material/core';

import bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';

// App views
import { LoginModule } from './views/login/login.module';
import { HomeModule } from './views/home/home.module';
import { DirectoryModule } from './views/directory/directory.module';
import { ParcelsModule } from './views/parcels/parcels.module';
import { DirectoryItemModule } from './views/directory-item/directory-item.module';
import { ComposeEmailModule } from './views/email/compose-email/compose-email.module';

import { LayoutsModule } from './layouts/layouts.module';

import { ApiClientService } from './services/api-client.service';
import { UrlService } from './services/url.service';
import { EmailModule } from './views/email/email.module';
import { SettingsModule } from './views/settings/settings.module';
import { DocumentsModule } from './views/documents/documents.module';
import { CustomStyleService } from './services/custom-style.service';
import { EmptyRowService } from './services/empty-row.service';
import { AdminModule } from './views/admin/admin.module';
import { IncidentReportsModule } from './views/incident-reports/incident-reports.module';
import { KeyLogModule } from './views/key-log/key-log.module';
import { PassOnLogModule } from './views/pass-on-log/pass-on-log.module';
import { ServiceRequestModule } from './views/service-request/service-request.module';
import { DailyReportsModule } from './views/daily-reports/daily-reports.module';
import { AngularMaterialImportModule } from './components/angular-material-import-module/angular-material-import.module';
import { AccountPermissionsService } from './services/account-permissions.service';
import { ClassifiedsModule } from './views/classifieds/classifieds.module';
import { NoticesModule } from './views/notices/notices.module';
import { ProfileModule } from './views/profile/profile.module';
import { AmenitiesModule } from './views/amenities/amenities.module';
import { SettingsAmenitiesModule } from './views/settings-amenities/settings-amenities.module';
import { WebsiteBuilderModule } from './views/website-builder/website-builder.module';
import { ResidentService } from './services/resident.service';
import { ResidentServicesService } from './services/resident-services.service';
import { SettingsParkingPassesModule } from './views/settings-parking-passes/settings-parking-passes.module';
import { ParkingPassModule } from './views/parking-pass/parking-pass.module';
import { FilterPipeModule } from './pipes/filter-pipe/filter-pipe.module';
import { ConfirmDeactivateGuard } from './services/email.service';
import { SettingsParcelsModule } from './views/settings-parcels/settings-parcels.module';
import { ReportGeneratorModule } from './views/report-generator/report-generator.module';
import { Nl2BrPipeModule } from './pipes/nl2br-pipe/nl2br-pipe.module';
import { ExitPageGuard } from './exit.page.guard';
import { ConfirmReportModalDeactivateGuard } from './services/report-generator.service';
import { CookieService } from 'app/services/cookie.service';
import { BuildingSetupModule } from './views/admin/property/building-setup/building-setup.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SettingsDirectoryModule } from './views/settings-directory/settings-directory.module';
import { FrontDeskInstructionsModule } from './views/front-desk-instructions/front-desk-instructions.module';
import { SettingsKeyLogModule } from './views/settings-key-log/settings-key-log.module';
import { SettingsIntegrationsModule } from './views/settings-integrations/settings-integrations.module';
import { ZendescService } from './services/zendesc.service';
import { MaintenanceCalendarModule } from './views/maintenance-calendar/maintenance-calendar.module';
import { ClassifiedsMccModule } from './views/classifieds-mcc/classifieds-mcc.module';
import { environment } from '../environments/environment';
import { BuildingApiService } from './services/api/building-api.service';
import { SettingsIncidentReportModule } from './views/settings-incident-report/settings-incident-report.module';
import { NpmiApiService } from './services/api/npmi-api.service';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { SettingsPassOnLogModule } from './views/settings-pass-on-log/settings-pass-on-log.module';
import { PassOnLogApiService } from './services/api/pass-on-log-api.service';
import { AuthApiService } from './services/api/auth-api.service';
import { VersionService } from './services/version.service';
import { VersionGuard } from './version.guard';
import { AuthTokenService } from './services/auth-token.service';
import { CommunityApiService } from './services/api/community-api.service';
import { UserStatisticsModule } from './views/user-statistics/user-statistics.module';
import { StripeAccountsModule } from './views/stripe-accounts/stripe-accounts.module';
import { ReportBuilderApiService } from './services/api/report-builder-api.service';
import { AppModalService } from './services/app-modal.service';
import { SettingsEmailMergeModule } from './views/settings-email-merge/settings-email-merge.module';
import { SettingsSmsModule } from './views/settings-sms/settings-sms.module';
import { AnnouncementApiService } from './services/api/announcement-api.service';
import { AnnouncementsModule } from './views/announcements/announcements.module';
import { ResolversModule } from './resolvers/resolvers.module';
import { FileUploadApiService } from './services/api/file-upload-api.service';
import { ApplyTokenInterceptor } from './interceptors/applyTokenInterceptor';
import { AlertService } from './services/alert.service';
import { UnsavedDataGuard } from './services/guards/unsaved-data-guard.service';
import { AmenityApiService } from './services/api/amenity-api.service';
import { ServiceRequestApiService } from './services/api/service-request-api.service';
import { VendorApiService } from './services/api/vendor-api.service';
import {NotificationsApiService} from './services/api/notifications-api.service';
import {DocumentApiService} from './services/api/document-api.service';
import {SmsNotificationApiService} from './services/api/sms-notification-api.service';

const bugsnagClient = environment.production ? bugsnag('70a6c0a52e6ed5f484bc5f56d79933a8') : null;

// create a factory which will return the bugsnag error handler
export function errorHandlerFactory() {
    if (environment.production) {
        bugsnagClient.app = {
            releaseStage: environment.production ? 'production' : 'staging'
        };
        if (localStorage.getItem('userData')) {
            bugsnagClient.user = {
                name: JSON.parse(localStorage.getItem('userData')).fullName,
                email: JSON.parse(localStorage.getItem('userData')).email
            };
        }
        return new BugsnagErrorHandler(bugsnagClient);
    } else
        return new ErrorHandler();
}

@NgModule({
    imports: [
        BrowserModule,
        HttpClientModule,
        BrowserAnimationsModule,
        FormsModule,
        HttpModule,
        HttpClientModule,
        AngularMaterialImportModule,

        LayoutsModule,

        // Views
        HomeModule,
        LoginModule,
        DirectoryModule,
        DirectoryItemModule,
        ParcelsModule,
        FrontDeskInstructionsModule,
        IncidentReportsModule,
        ServiceRequestModule,
        KeyLogModule,
        ClassifiedsModule,
        NoticesModule,
        PassOnLogModule,
        DailyReportsModule,
        EmailModule,
        AmenitiesModule,
        ComposeEmailModule,
        SettingsModule,
        DocumentsModule,
        AdminModule,
        ProfileModule,
        SettingsAmenitiesModule,
        SettingsParkingPassesModule,
        ParkingPassModule,
        FilterPipeModule,
        WebsiteBuilderModule,
        SettingsParcelsModule,
        ReportGeneratorModule,
        Nl2BrPipeModule,
        BuildingSetupModule,
        SettingsDirectoryModule,
        SettingsKeyLogModule,
        SettingsIntegrationsModule,
        MaintenanceCalendarModule,
        ClassifiedsMccModule,
        SettingsIncidentReportModule,
        SettingsPassOnLogModule,
        UserStatisticsModule,
        StripeAccountsModule,
        SettingsEmailMergeModule,
        SettingsSmsModule,
        AnnouncementsModule,
        ResolversModule,

        RouterModule.forRoot(ROUTES)
    ],
    declarations: [
        AppComponent,
    ],
    exports: [
        BrowserAnimationsModule
    ],
    providers: [
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy,
        },
        AuthGuard,
        GuestGuard,
        LoginGuard,
        NavGuard,
        NavGuardSettings,
        CanCreateGuard,
        ExitPageGuard,
        ConfirmDeactivateGuard,
        ApiClientService,
        AuthTokenService,
        BuildingApiService,
        CommunityApiService,
        NpmiApiService,
        PassOnLogApiService,
        ReportBuilderApiService,
        AuthApiService,
        AnnouncementApiService,
        FileUploadApiService,
        NotificationsApiService,
        AmenityApiService,
        ServiceRequestApiService,
        VendorApiService,
        UrlService,
        CustomStyleService,
        EmptyRowService,
        AccountPermissionsService,
        ResidentService,
        ResidentServicesService,
        {provide: MAT_DATE_LOCALE, useValue: 'en-GB'},
        CookieService,
        ConfirmReportModalDeactivateGuard,
        {provide: ErrorHandler, useFactory: errorHandlerFactory},
        ZendescService,
        VersionService,
        VersionGuard,
        AppModalService,
        AlertService,
        UnsavedDataGuard,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApplyTokenInterceptor,
            multi: true
        },
        DocumentApiService,
        SmsNotificationApiService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}



// WEBPACK FOOTER //
// /app/src/app/app.module.ts